import { newsletterKindEnum, newsletters } from '@markku/db'
import { createInsertSchema, createSelectSchema } from 'drizzle-zod'
import { z } from 'zod'
import { audienceFilterSchema } from './audienceFilter'
import { phoneNumberSchema } from './common'

// Automation template keys. The recommendation mail has one template per
// recommended service (own voucher value, own copy); the expiry reminder is
// shared by all services.
export const SERVICE_RECOMMENDATION_REMINDER_KEY =
  'service_recommendation_reminder'
export const serviceRecommendationKey = (service: string) =>
  `service_recommendation:${service}`

export const newsletterAutomationSettingsSchema = z.object({
  enabled: z.boolean(),
  delayDays: z.number().int().min(0),
  voucherValue: z.number().positive(),
  voucherValidDays: z.number().int().min(1),
  reminderDaysBefore: z.number().int().min(0),
  // Where "Varaa aika" points for this service, also in the reminder. The
  // voucher code and UTM tags are appended at send time. Optional in the
  // shape (the reminder template has none), required to enable a service
  // template.
  bookingUrl: z.string().url().optional(),
})

export type NewsletterAutomationSettings = z.infer<
  typeof newsletterAutomationSettingsSchema
>

export const DEFAULT_AUTOMATION_SETTINGS: NewsletterAutomationSettings = {
  enabled: false,
  delayDays: 2,
  voucherValue: 50,
  voucherValidDays: 14,
  reminderDaysBefore: 2,
}

export const selectNewsletterSchema = createSelectSchema(newsletters, {
  // Internal-prefixed field: optional so it validates when stripped for
  // non-privileged users (see CLAUDE.md internal-field convention).
  internalName: z.string().nullable().optional(),
})
export const insertNewsletterSchema = createInsertSchema(newsletters).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
})

// Back-compat alias: newsletters referenced `newsletterAudienceFilterSchema`
// before the shared audience module was extracted.
export const newsletterAudienceFilterSchema = audienceFilterSchema

export const upsertNewsletterSchema = z.object({
  subject: z.string().min(1),
  internalName: z.string().optional(),
  designJson: z.any().optional(),
  html: z.string().optional(),
  audienceFilter: audienceFilterSchema.optional(),
  senderUserId: z.number().nullable().optional(),
  kind: z.enum(newsletterKindEnum.enumValues).optional(),
  automationKey: z.string().nullable().optional(),
  automationSettings: newsletterAutomationSettingsSchema.optional(),
})

// Profile behind the {{sender…}} merge tags; name, email and phone come from
// the user record itself.
export const senderProfileSchema = z.object({
  jobTitle: z.string().nullable(),
  // Key required like the siblings; the pipe normalises to +358 form
  publicPhoneNumber: z.string().nullable().pipe(phoneNumberSchema),
  linkedinUrl: z.string().url().nullable(),
  photoUrl: z.string().url().nullable(),
})

export type Newsletter = z.infer<typeof selectNewsletterSchema>
export type SenderProfile = z.infer<typeof senderProfileSchema>
export type InsertNewsletter = z.infer<typeof insertNewsletterSchema>
export type UpsertNewsletter = z.infer<typeof upsertNewsletterSchema>
export type NewsletterAudienceFilter = z.infer<typeof audienceFilterSchema>
